import React, { useEffect, useState } from "react";
import { Loader2, Bot, TrendingUp, Users, MessageCircle, PhoneCall, X, IndianRupee, RefreshCw, Beaker } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const Pill = ({ icon: Icon, label, value, color = "text-[#1E1B4B]" }) => (
  <div className="bg-white border border-gray-100 rounded-xl px-3 py-2 flex items-center gap-2">
    <Icon size={14} className="text-[#475569] flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[#94a3b8] font-semibold">{label}</p>
      <p className={`text-base font-bold ${color}`}>{value}</p>
    </div>
  </div>
);

const Bucket = ({ name, data, accent }) => (
  <div className={`rounded-2xl border ${accent} p-4`}>
    <p className="text-[10px] uppercase tracking-wider text-[#475569] font-bold mb-2">{name}</p>
    <p className="text-2xl font-extrabold text-[#1E1B4B]">{data.conversion_pct}%</p>
    <p className="text-[11px] text-[#94a3b8] mt-0.5">conversion</p>
    <div className="mt-2 pt-2 border-t border-gray-200/70 text-[11px] text-[#475569] space-y-0.5">
      <div className="flex justify-between"><span>cohort</span><span className="font-semibold text-[#1E1B4B]">{data.cohort}</span></div>
      <div className="flex justify-between"><span>bookings</span><span className="font-semibold text-[#1E1B4B]">{data.bookings}</span></div>
      <div className="flex justify-between"><span>revenue</span><span className="font-semibold text-emerald-600">{inr(data.revenue_inr)}</span></div>
    </div>
  </div>
);

const DoozySalesAnalyticsCard = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await authAxios.get(`${API_URL}/api/admin/doozysales/stats?days=30`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load DoozySales stats");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading && !data) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-center" data-testid="doozysales-card-loading">
        <Loader2 size={20} className="animate-spin text-[#DC283C]" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-sm text-rose-700" data-testid="doozysales-card-error">{err}</div>
    );
  }
  if (!data) return null;

  const ab = data.ab_test || {};
  const treatment = ab.treatment || {};
  const control = ab.control || {};
  const liftAbs = ab.lift_abs_pct_points;
  const liftRel = ab.lift_relative_pct;
  const liftPositive = (liftAbs || 0) > 0;
  const liftColor = liftAbs == null || liftAbs === 0 ? "text-[#475569]" : liftPositive ? "text-emerald-600" : "text-rose-600";

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden" data-testid="doozysales-analytics-card">
      <div className="px-5 py-4 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 text-white flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
          <Bot size={20} />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-bold leading-tight">DoozySales · last 30d</h3>
          <p className="text-[11px] text-white/80 flex items-center gap-1.5"><Beaker size={11} /> A/B holdback @ {ab.control_pct_target ?? 20}% control</p>
        </div>
        <button onClick={load} className="px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-[11px] font-bold flex items-center gap-1" data-testid="doozysales-card-refresh">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Outbound + handoff funnel */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <Pill icon={Users} label="Touched" value={data.sent.toLocaleString("en-IN")} />
          <Pill icon={MessageCircle} label="Replied" value={data.replied.toLocaleString("en-IN")} />
          <Pill icon={PhoneCall} label="Callback" value={data.callback_requested.toLocaleString("en-IN")} color="text-amber-600" />
          <Pill icon={X} label="Lost" value={data.lost.toLocaleString("en-IN")} color="text-rose-500" />
          <Pill icon={IndianRupee} label="Revenue" value={inr(data.revenue_inr)} color="text-emerald-600" />
        </div>

        {/* A/B Lift */}
        <div className="bg-[#FFFAF5] rounded-2xl p-4 border border-amber-100" data-testid="doozysales-lift-block">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <p className="text-[11px] uppercase tracking-wider text-[#475569] font-bold flex items-center gap-1.5">
              <TrendingUp size={12} /> Causal lift (treatment vs control)
            </p>
            {!ab.is_significant_n && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                small N · directional only
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Bucket name="Treatment (sent)" data={treatment} accent="border-emerald-200 bg-white" />
            <Bucket name="Control (silent)" data={control} accent="border-gray-200 bg-white" />
            <div className="rounded-2xl border border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 to-rose-50 p-4 flex flex-col">
              <p className="text-[10px] uppercase tracking-wider text-[#475569] font-bold mb-2">Lift</p>
              <p className={`text-3xl font-extrabold ${liftColor}`} data-testid="doozysales-lift-abs">
                {liftAbs == null ? "—" : `${liftAbs > 0 ? "+" : ""}${liftAbs}pp`}
              </p>
              <p className="text-[11px] text-[#94a3b8] mt-0.5">absolute</p>
              {liftRel != null && (
                <p className={`text-xs font-semibold mt-2 pt-2 border-t border-gray-200/70 ${liftColor}`}>
                  {liftRel > 0 ? "+" : ""}{liftRel}% relative
                </p>
              )}
            </div>
          </div>
          <p className="text-[11px] text-[#94a3b8] mt-3 leading-relaxed">
            Each eligible warm-idle lead is assigned by SHA1(phone) — control sees no DoozySales nudge so we can attribute true causal lift, not just correlation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DoozySalesAnalyticsCard;
